import type { ComponentType } from 'react';

type Icon = ComponentType<{ className?: string }>;

/** Something search can jump to that is not a record: a page, or a tab within one. */
export interface PaletteDestination {
  /** Unique across destinations; also what the usage event reports. */
  key: string;
  label: string;
  /** The grey text on the right: a page's sidebar section, or a tab's page. */
  detail: string;
  href: string;
  icon: Icon;
  /** Extra words it should be found by that are not on screen. */
  keywords?: string;
  /** Tabs only show once something is typed; the empty box lists pages. */
  isSubTab?: boolean;
}

interface AdminSubTab {
  /** The page's route key, as in the sidebar. */
  page: string;
  /** The query param the page keeps its tab in (see useUrlTabState). */
  param: string;
  key: string;
  /** As the page's own tab button reads, so search and page agree. */
  label: string;
  isDefault?: boolean;
  keywords?: string;
}

/**
 * Every tab an admin page keeps in its URL. Only tabs that live in the query string can be
 * linked to, which is all of them today; a tab that someday moves to local state would have
 * to come off this list.
 *
 * The keys must match each page's own `*_TABS` list. A stale key is harmless — the page
 * falls back to its default tab, the same as for a hand-edited link — but it would land
 * on the wrong tab, so a renamed tab is renamed here too.
 */
export const ADMIN_SUB_TABS: readonly AdminSubTab[] = [
  { page: 'students', param: 'tab', key: 'directory', label: 'All students', isDefault: true },
  { page: 'students', param: 'tab', key: 'roster', label: 'Pickup roster', keywords: 'pickup check out' },
  { page: 'students', param: 'tab', key: 'day', label: 'Day roster', keywords: 'attendance who is coming' },
  { page: 'students', param: 'tab', key: 'history', label: 'History', keywords: 'attendance history' },
  { page: 'students', param: 'tab', key: 'photos', label: 'Headshots', keywords: 'photos' },

  { page: 'programs', param: 'tab', key: 'programs', label: 'Programs', isDefault: true },
  { page: 'programs', param: 'tab', key: 'line-items', label: 'Line Item Configs', keywords: 'add-ons opt-in pricing catalog' },

  { page: 'enrollments', param: 'view', key: 'summary', label: 'Summary', isDefault: true, keywords: 'pivot breakdown' },
  { page: 'enrollments', param: 'view', key: 'details', label: 'Details', keywords: 'list' },

  { page: 'finances', param: 'tab', key: 'invoices', label: 'Invoices', isDefault: true },
  { page: 'finances', param: 'tab', key: 'payments', label: 'Payments', keywords: 'zelle card ach' },
  { page: 'finances', param: 'tab', key: 'carts', label: 'Bundles', keywords: 'carts pay together bundle payment' },
  { page: 'finances', param: 'tab', key: 'refunds', label: 'Refunds' },
  { page: 'finances', param: 'tab', key: 'debt', label: 'Debt', keywords: 'overdue outstanding aging' },

  { page: 'coupons', param: 'tab', key: 'templates', label: 'Templates', isDefault: true, keywords: 'coupon templates' },
  { page: 'coupons', param: 'tab', key: 'printable', label: 'Printable', keywords: 'print coupons' },
  { page: 'coupons', param: 'tab', key: 'coupons', label: 'Coupons' },
  { page: 'coupons', param: 'tab', key: 'discounts', label: 'Discounts', keywords: 'rules eligibility' },

  { page: 'balances', param: 'tab', key: 'activity', label: 'Activity feed', isDefault: true, keywords: 'bhcs cash' },
  { page: 'balances', param: 'tab', key: 'parents', label: 'Per parent', keywords: 'bhcs cash' },

  { page: 'insights', param: 'tab', key: 'collection', label: 'Monthly Collection', isDefault: true },
  { page: 'insights', param: 'tab', key: 'enrollment', label: 'Enrollment Progress' },
  { page: 'insights', param: 'tab', key: 'funnel', label: 'Enrollment Funnel' },
  { page: 'insights', param: 'tab', key: 'personas', label: 'Family Personas' },

  { page: 'analytics', param: 'tab', key: 'events', label: 'Events', isDefault: true },
  { page: 'analytics', param: 'tab', key: 'statistics', label: 'Stats', keywords: 'statistics' },
  { page: 'analytics', param: 'tab', key: 'charts', label: 'Trends', keywords: 'charts' },

  { page: 'email', param: 'tab', key: 'history', label: 'Email History', isDefault: true },
  { page: 'email', param: 'tab', key: 'templates', label: 'Email Templates' },
  { page: 'email', param: 'tab', key: 'lists', label: 'Email Lists' },

  { page: 'system', param: 'tab', key: 'changelog', label: 'Changelog', isDefault: true, keywords: 'release notes version' },
  { page: 'system', param: 'tab', key: 'app-settings', label: 'App Settings', keywords: 'settings' },
  { page: 'system', param: 'tab', key: 'flag-cohorts', label: 'Flag Cohorts', keywords: 'feature flags rollout' },
  { page: 'system', param: 'tab', key: 'ai-cli', label: 'AI Connectors', keywords: 'cli mcp' },
];

interface SidebarPage {
  key: string;
  label: string;
  /** Its sidebar section; empty for the top-level pages. */
  section: string;
  icon: Icon;
}

/**
 * The sidebar's pages followed by their tabs. A tab comes along only when its page does, so
 * a page the sidebar hides (Meeting Signups before any round exists) hides its tabs too.
 * A default tab links to the bare page, the URL the page itself keeps for it.
 */
export function buildPaletteDestinations(pages: SidebarPage[]): PaletteDestination[] {
  const byKey = new Map(pages.map(page => [page.key, page]));
  const pageDestinations = pages.map(page => ({
    key: page.key,
    label: page.label,
    detail: page.section,
    href: `/admin-app/${page.key}`,
    icon: page.icon,
  }));
  const tabDestinations = ADMIN_SUB_TABS.flatMap(tab => {
    const page = byKey.get(tab.page);
    if (!page) return [];
    return [{
      key: `${tab.page}:${tab.key}`,
      label: tab.label,
      detail: page.label,
      href: tab.isDefault
        ? `/admin-app/${tab.page}`
        : `/admin-app/${tab.page}?${tab.param}=${encodeURIComponent(tab.key)}`,
      icon: page.icon,
      keywords: [page.section, tab.keywords].filter(Boolean).join(' '),
      isSubTab: true,
    }];
  });
  return [...pageDestinations, ...tabDestinations];
}
